import { urqlClient } from '@/lib/urqlClient'
import { Client, AnyVariables, TypedDocumentNode } from 'urql'
import { print } from 'graphql'


/**
 * Adapter that wraps urql Client to provide a GraphQLClient-compatible
 * `.request()` method (similar surface to graphql-request) while exposing
 * the underlying urql Client methods.
 */
class UrqlClientAdapter {
  private client: Client

  constructor(client: Client) {
    this.client = client
  }

  query<T = unknown, V extends AnyVariables = AnyVariables>(
    query: TypedDocumentNode<T, V> | string,
    variables?: V,
    context?: Partial<RequestInit>,
  ) {
    return this.client.query(query, variables ?? ({} as V), context)
  }

  mutation<T = unknown, V extends AnyVariables = AnyVariables>(
    query: TypedDocumentNode<T, V> | string,
    variables?: V,
    context?: Partial<RequestInit>,
  ) {
    return this.client.mutation(query, variables ?? ({} as V), context)
  }

  subscription<T = unknown, V extends AnyVariables = AnyVariables>(
    query: TypedDocumentNode<T, V> | string,
    variables?: V,
    context?: Partial<RequestInit>,
  ) {
    return this.client.subscription(query, variables ?? ({} as V), context)
  }

  async request<
    T = Record<string, unknown>,
    V extends AnyVariables = AnyVariables,
  >(
    query: TypedDocumentNode | string,
    variables?: V,
    headers?: Record<string, string>,
  ): Promise<T> {
    const isMutation = this.isMutationOperation(query)
    const context = {
      fetchOptions: headers ? { headers } : undefined,
    }

    const typedQuery = query as
      | TypedDocumentNode<unknown, AnyVariables>
      | string
    const result = isMutation
      ? await this.client.mutation(typedQuery, variables, context).toPromise()
      : await this.client.query(typedQuery, variables, context).toPromise()

    if (result.error) {
      throw result.error
    }

    if (!result.data) {
      throw new Error('No data returned from query')
    }

    return result.data as T
  }

  private isMutationOperation(query: TypedDocumentNode | string): boolean {
    if (typeof query === 'string') {
      return query.trim().toLowerCase().startsWith('mutation')
    }

    try {
      const queryString = print(query)
      const normalized = queryString.trim().toLowerCase()
      return (
        normalized.startsWith('mutation') || normalized.includes('\nmutation')
      )
    } catch {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const doc = query as any
      if (
        doc.definitions &&
        Array.isArray(doc.definitions) &&
        doc.definitions.length > 0
      ) {
        const firstDefinition = doc.definitions[0]
        if (firstDefinition && typeof firstDefinition === 'object') {
          if (firstDefinition.operation === 'mutation') {
            return true
          }
          if (
            firstDefinition.kind === 'OperationDefinition' &&
            firstDefinition.operation === 'mutation'
          ) {
            return true
          }
        }
      }
      const queryString = doc.loc?.source?.body
      if (typeof queryString === 'string') {
        const normalized = queryString.trim().toLowerCase()
        return (
          normalized.startsWith('mutation') || normalized.includes('\nmutation')
        )
      }
    }

    return false
  }

  setHeaders(_headers?: Record<string, string>): void {
    // Headers are managed by urql authExchange, no-op here.
  }
}

// Stays loosely typed because legacy call sites reach for properties the
// adapter doesn't actually expose (e.g. `.subscribe(...)` in jobs hooks,
// or Refine `hasuraDataProvider` CRUD methods that expect graphql-request's
// `GraphQLClient`). Tightening this export will be done as those call sites
// migrate. Service modules type their parameter as `GqlClient` directly —
// see `src/types/gqlClient.ts`.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const GQL_CLIENT: any = new UrqlClientAdapter(urqlClient)
